import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ACCESSEligibilityResultCode } from '@/types/registration';
import { isWregDevPanelEnabled } from '@/components/webreg/WregDevPanel';
import { isWregEligCode } from '@/components/webreg/wregEligibility';
import { WREG_ORDER, type WregStep } from '@/components/webreg/wregSteps';
import {
  WREG_DISCLOSURE_VARIANTS,
  WREG_SCENARIOS,
  WREG_SCENARIO_CODE,
  type WregDisclosure,
  type WregScenarioId,
} from '@/components/webreg/wregScenarios';

const params = () => new URLSearchParams(window.location.search);

export const readStepParam = (): WregStep | null => {
  const raw = params().get('step');
  return WREG_ORDER.includes(raw as WregStep) ? (raw as WregStep) : null;
};

export const readScenarioParam = (): WregScenarioId | null => {
  const raw = params().get('scenario');
  return WREG_SCENARIOS.some((s) => s.id === raw) ? (raw as WregScenarioId) : null;
};

export const readDisclosureParam = (): WregDisclosure | null => {
  const raw = params().get('disclosure');
  return WREG_DISCLOSURE_VARIANTS.includes(raw as WregDisclosure) ? (raw as WregDisclosure) : null;
};

export const readCodeParam = (): ACCESSEligibilityResultCode | null => {
  const raw = params().get('code') ?? '';
  return isWregEligCode(raw) ? raw : null;
};

export const readLiveParam = (): boolean => params().get('cms') === 'live';

export type WregDevState = {
  enabled: boolean;
  scenario: WregScenarioId;
  setScenario: (id: WregScenarioId) => void;
  mockCode: ACCESSEligibilityResultCode;
  setMockCode: (code: ACCESSEligibilityResultCode) => void;
  live: boolean;
  setLive: (live: boolean) => void;
  disclosure: WregDisclosure;
  setDisclosure: (disclosure: WregDisclosure) => void;
  entryStep: WregStep | null;
};

const WregDevContext = createContext<WregDevState | undefined>(undefined);

const noop = () => undefined;

export const WREG_DEV_OFF: WregDevState = {
  enabled: false,
  scenario: 'happy',
  setScenario: noop,
  mockCode: WREG_SCENARIO_CODE.happy,
  setMockCode: noop,
  live: true,
  setLive: noop,
  disclosure: 'A',
  setDisclosure: noop,
  entryStep: null,
};

const WregDevProvider = ({ children }: { children: ReactNode }) => {
  const enabled = isWregDevPanelEnabled();
  const [initial] = useState(() => {
    const scenario = readScenarioParam() ?? 'happy';
    return {
      scenario,
      mockCode: readCodeParam() ?? WREG_SCENARIO_CODE[scenario],
      disclosure: readDisclosureParam() ?? ('A' as WregDisclosure),
      entryStep: readStepParam(),
      live: enabled ? readLiveParam() : true,
    };
  });

  const [scenario, setScenario] = useState<WregScenarioId>(initial.scenario);
  const [mockCode, setMockCode] = useState<ACCESSEligibilityResultCode>(initial.mockCode);
  const [live, setLive] = useState(initial.live);
  const [disclosure, setDisclosure] = useState<WregDisclosure>(initial.disclosure);

  const value = useMemo<WregDevState>(
    () =>
      enabled
        ? {
            enabled,
            scenario,
            setScenario,
            mockCode,
            setMockCode,
            live,
            setLive,
            disclosure,
            setDisclosure,
            entryStep: initial.entryStep,
          }
        : WREG_DEV_OFF,
    [enabled, scenario, mockCode, live, disclosure, initial.entryStep],
  );

  return <WregDevContext.Provider value={value}>{children}</WregDevContext.Provider>;
};

const useWregDev = (): WregDevState => useContext(WregDevContext) ?? WREG_DEV_OFF;

export const wregDevSearch = (dev: WregDevState): string => {
  if (!dev.enabled) return '';
  const next = new URLSearchParams();
  if (dev.scenario !== 'happy') next.set('scenario', dev.scenario);
  if (dev.disclosure !== 'A') next.set('disclosure', dev.disclosure);
  if (dev.live) next.set('cms', 'live');
  const query = next.toString();
  return query === '' ? '' : `?${query}`;
};

export { WregDevProvider, useWregDev };
